import React, { useEffect, useState } from "react";
import type { ActionBrief } from "../../lib/actionBriefs";
import { getLearningLinks } from "../../lib/learningLinks";
import { colors } from "../../lib/designTokens";
import { supabase } from "../../lib/supabase";
import OpportunityActionBriefPanel from "../opportunities/OpportunityActionBriefPanel";

type OpportunityCardProps = {
  item: {
    competitor: string;
    total_gap_count: number;
    primary_category: string;
    category_label?: string;
    recommended_action?: string;
    impact_level: "High" | "Medium" | "Low";
    run_id?: string;
    run_date?: string | null;
  };
  demoMode?: boolean;
  customerBrand?: string;
  isLeaderAccount?: boolean;
  rank: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;

  accountId: string;
  runId?: string | null;
  accountCategory?: string;
  completedActionBrief?: ActionBrief | null;
  onCompletionChange?: (brief: ActionBrief) => void;
};

type CompletionMetrics = {
  currentRun: number;
  last30: number;
  ytd: number;
  allTime: number;
  currentYear: number;
};

const EMPTY_COMPLETION_METRICS: CompletionMetrics = {
  currentRun: 0,
  last30: 0,
  ytd: 0,
  allTime: 0,
  currentYear: new Date().getFullYear(),
};

function normalize(value?: string) {
  return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizeCategory(category?: string) {
  const value = (category || "").trim().toLowerCase();

  switch (value) {
    case "category_discovery":
    case "discovery":
    case "brand interpretation":
    case "publishing use case":
      return "category_discovery";

    case "alternatives_replacements":
      return "alternatives_replacements";

    case "comparisons":
    case "comparison":
      return "comparisons";

    case "use_case_fit":
    case "consideration":
    case "core category":
      return "use_case_fit";

    case "pain_point_led":
      return "pain_point_led";

    case "purchase_shortlist":
    case "recommendation":
    case "strategic":
      return "purchase_shortlist";

    default:
      return "category_discovery";
  }
}

function formatCategory(category?: string) {
  const value = normalizeCategory(category);

  switch (value) {
    case "category_discovery":
      return "Category Discovery";
    case "alternatives_replacements":
      return "Alternatives";
    case "comparisons":
      return "Comparisons";
    case "use_case_fit":
      return "Use-Case Fit";
    case "pain_point_led":
      return "Pain Points";
    case "purchase_shortlist":
      return "Purchase Shortlist";
    default:
      return "Category Discovery";
  }
}

function getImpactStyles(level: string) {
  switch (level) {
    case "High":
      return {
        background: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#b91c1c",
      };
    case "Medium":
      return {
        background: "#fffbeb",
        border: "1px solid #fde68a",
        color: "#b45309",
      };
    default:
      return {
        background: "#ecfdf5",
        border: "1px solid #bbf7d0",
        color: "#047857",
      };
  }
}

function getCompletionWindows() {
  const now = new Date();
  const last30 = new Date(now);
  last30.setDate(now.getDate() - 30);

  const ytdStart = new Date(now.getFullYear(), 0, 1);

  return {
    last30Start: last30.toISOString(),
    ytdStart: ytdStart.toISOString(),
    currentYear: now.getFullYear(),
  };
}

export default function OpportunityCard({
  item,
  demoMode,
  customerBrand,
  isLeaderAccount,
  rank,
  total,
  onPrev,
  onNext,
  canPrev,
  canNext,
  accountId,
  runId,
  accountCategory,
  completedActionBrief,
  onCompletionChange,
}: OpportunityCardProps) {
  const categoryKey = normalizeCategory(item.primary_category);
  const categoryLabel = formatCategory(item.primary_category);
  const links = getLearningLinks(categoryKey);
  const primaryLink = links?.[0];
  const [completionMetrics, setCompletionMetrics] = useState<CompletionMetrics>(EMPTY_COMPLETION_METRICS);
  const [completionStatus, setCompletionStatus] = useState<"loading" | "ready" | "error">("loading");

  const isLeaderMode = !!isLeaderAccount;

  const competitorName = demoMode ? "Top Competitor 🔒" : item.competitor;
  const displayBrand = demoMode ? "Your brand" : customerBrand || "Your brand";

  const impactStyles = getImpactStyles(item.impact_level);

  const headline = isLeaderMode
  ? `Defend your lead in ${categoryLabel.toLowerCase()} opportunities.`
  : `You’re losing visibility in ${categoryLabel.toLowerCase()} opportunities to ${competitorName}.`;

const supportingCopy = isLeaderMode
  ? `${competitorName} is the closest challenger in this area. Protect your position by strengthening comparison, FAQ, and recommendation-stage content before competitors narrow the gap.`
  : `${competitorName} is showing up where ${displayBrand} has room to gain more AI visibility.`;

const actionLabel = isLeaderMode
  ? `Protect ${categoryLabel.toLowerCase()} visibility`
  : `Improve ${categoryLabel.toLowerCase()} visibility`;

  useEffect(() => {
    let isMounted = true;

    async function countCompletedOpportunities({ gte = null, activeRunId = null }: { gte?: string | null; activeRunId?: string | null }) {
      let query = supabase
        .from("opportunity_action_briefs")
        .select("id", { count: "exact", head: true })
        .eq("account_id", accountId)
        .not("marked_complete_at", "is", null);

      if (gte) query = query.gte("marked_complete_at", gte);
      if (activeRunId) query = query.eq("run_id", activeRunId);

      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    }

    async function loadCompletionMetrics() {
      if (!accountId) {
        setCompletionMetrics(EMPTY_COMPLETION_METRICS);
        setCompletionStatus("ready");
        return;
      }

      setCompletionStatus((current) => (current === "ready" ? "ready" : "loading"));

      try {
        const windows = getCompletionWindows();
        const resolvedRunId = runId || item.run_id || null;

        const [currentRun, last30, ytd, allTime] = await Promise.all([
          resolvedRunId
            ? countCompletedOpportunities({ activeRunId: resolvedRunId })
            : Promise.resolve(0),
          countCompletedOpportunities({ gte: windows.last30Start }),
          countCompletedOpportunities({ gte: windows.ytdStart }),
          countCompletedOpportunities({}),
        ]);

        if (!isMounted) return;

        setCompletionMetrics({
          currentRun,
          last30,
          ytd,
          allTime,
          currentYear: windows.currentYear,
        });
        setCompletionStatus("ready");
      } catch (error) {
        console.warn("[OpportunityCard] Completed opportunity metrics failed", error);
        if (isMounted) {
          setCompletionMetrics(EMPTY_COMPLETION_METRICS);
          setCompletionStatus("error");
        }
      }
    }

    loadCompletionMetrics();
    const refreshTimer = window.setInterval(loadCompletionMetrics, 10000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
    };
  }, [accountId, runId, item.run_id]);

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 24,
        background: "#ffffff",
        padding: 22,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#64748b",
              marginBottom: 5,
            }}
          >
            {isLeaderMode ? "Leader Defense" : "Opportunity Detail"}
          </div>

          <div
            style={{
              fontSize: 13,
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Opportunity {rank} of {total}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <span
            style={{
              ...impactStyles,
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {item.impact_level} Impact
          </span>

          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            style={{
              border: "1px solid #e2e8f0",
              background: canPrev ? "#ffffff" : "#f8fafc",
              color: canPrev ? "#334155" : "#cbd5e1",
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 700,
              cursor: canPrev ? "pointer" : "not-allowed",
            }}
            aria-label="Previous opportunity"
          >
            ← Prev
          </button>

          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            style={{
              border: "1px solid #e2e8f0",
              background: canNext ? "#ffffff" : "#f8fafc",
              color: canNext ? "#334155" : "#cbd5e1",
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 700,
              cursor: canNext ? "pointer" : "not-allowed",
            }}
            aria-label="Next opportunity"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Primary identity */}
      <div
        style={{
          borderTop: "1px solid #f1f5f9",
          paddingTop: 18,
        }}
      >
        <div
          style={{
            fontSize: 28,
            lineHeight: 1.1,
            fontWeight: 800,
            letterSpacing: "-0.03em",
            color: "#020617",
            marginBottom: 6,
          }}
        >
          {isLeaderMode ? displayBrand : competitorName}
        </div>

        <div
          style={{
            fontSize: 14,
            color: "#64748b",
            fontWeight: 500,
          }}
        >
          {isLeaderMode
            ? `Leading in ${categoryLabel.toLowerCase()}`
            : `Competing in ${categoryLabel.toLowerCase()}`}
        </div>
      </div>

      {/* Narrative */}
      <div
        style={{
          marginTop: 18,
          borderRadius: 18,
          background: isLeaderMode ? "#f0fdf4" : "#f8fafc",
          border: isLeaderMode ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
          padding: 18,
        }}
      >
        <div
          style={{
            fontSize: 18,
            lineHeight: 1.35,
            fontWeight: 750,
            color: "#0f172a",
            marginBottom: 8,
          }}
        >
          {headline}
        </div>

        <div
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "#475569",
          }}
        >
          {supportingCopy}
        </div>
      </div>

      {/* Metrics */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
          marginTop: 16,
          color: "#475569",
          fontSize: 14,
        }}
      >
        <span>
          <strong style={{ color: "#0f172a", fontSize: 18 }}>
            {item.total_gap_count}
          </strong>{" "}
              {isLeaderMode ? "defense signals" : "buyer-journey gaps"}        </span>

        <span style={{ color: "#cbd5e1" }}>•</span>

        <span>{item.impact_level} priority</span>

        <span style={{ color: "#cbd5e1" }}>•</span>

        <span>{categoryLabel} focus</span>
      </div>

      <div
        style={{
          marginTop: 16,
          border: "1px solid #bbf7d0",
          borderRadius: 18,
          background: "#f0fdf4",
          padding: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#166534",
              }}
            >
              Completed opportunity validation
            </div>
            <div
              style={{
                color: "#475569",
                fontSize: 12,
                marginTop: 3,
              }}
            >
              Proof that optimization opportunities are being worked, not just identified.
            </div>
          </div>
          {completionStatus === "error" && (
            <div style={{ color: "#92400e", fontSize: 12, fontWeight: 700 }}>
              Metrics unavailable
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))",
            gap: 8,
          }}
        >
          {[
            ["This run", completionMetrics.currentRun],
            ["Last 30 days", completionMetrics.last30],
            [`${completionMetrics.currentYear} YTD`, completionMetrics.ytd],
            ["All time", completionMetrics.allTime],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              style={{
                border: "1px solid #bbf7d0",
                borderRadius: 14,
                background: "#ffffff",
                padding: "10px 12px",
              }}
            >
              <div style={{ color: "#166534", fontSize: 22, fontWeight: 850, lineHeight: 1 }}>
                {completionStatus === "loading" ? "—" : value}
              </div>
              <div style={{ marginTop: 4, color: "#64748b", fontSize: 11, fontWeight: 750 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: "1px solid #f1f5f9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {primaryLink ? (
          <a
            href={primaryLink.href}
            target={primaryLink.external ? "_blank" : undefined}
            rel={primaryLink.external ? "noreferrer" : undefined}
            style={{
              color: colors?.primary || "#2563eb",
              fontSize: 14,
              fontWeight: 750,
              textDecoration: "none",
            }}
          >
            {actionLabel} →
          </a>
        ) : (
          <span
            style={{
              fontSize: 14,
              color: "#94a3b8",
              fontWeight: 600,
            }}
          >
            Improve buyer journey visibility
          </span>
        )}

        <span
          style={{
            fontSize: 12,
            color: "#94a3b8",
            fontWeight: 600,
          }}
        >
          Based on completed organic prompts
        </span>
      </div>
      <p
  style={{
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.35,
  }}
>
  Radar shows the overall buyer-journey pattern. Opportunity Engine shows
 competitor-specific gaps by journey stage.
 </p>
<OpportunityActionBriefPanel
  accountId={accountId}
  runId={runId || item.run_id || null}
  productArea="competitive"
  initialActionBrief={completedActionBrief || null}
  onCompletionChange={onCompletionChange}
  opportunity={{
    title: isLeaderMode
      ? `Defend ${displayBrand} against ${competitorName}`
      : `Close gap vs ${competitorName}`,
    summary: isLeaderMode
      ? `${competitorName} is the closest challenger in ${categoryLabel}. Strengthen content and proof points to protect current visibility.`
      : `${competitorName} is showing up where ${displayBrand} has room to gain more AI visibility in ${categoryLabel}.`,
    recommendation:
      item.recommended_action ||
      (isLeaderMode
        ? `Strengthen comparison, FAQ, and recommendation-stage content to defend ${categoryLabel} visibility.`
        : `Strengthen content that explains why ${displayBrand} should be considered alongside or instead of ${competitorName} in ${categoryLabel}.`),
    category: accountCategory || item.category_label || item.primary_category,
    intent: item.primary_category,
    priority: item.impact_level,
    competitors: demoMode ? [] : [item.competitor],
    evidence: [
      `${item.total_gap_count} ${
        isLeaderMode ? "defense signals" : "buyer-journey gaps"
      }`,
      `${item.impact_level} priority`,
      `${categoryLabel} focus`,
    ],
  }}
  accountContext={{
    accountName: displayBrand,
    category: accountCategory || item.category_label || item.primary_category,
    competitors: demoMode ? [] : [item.competitor],
  }}
/>
    </div>
  );
}
